import { createSlice } from '@reduxjs/toolkit';
import {
  FETCH_STUDENT,
  SEARCH_DOCUMENTS,
  GET_DOCUMENT,
  EXPECTED_RECOMMENDATION,
  UPDATE_RECOMMENDATION_HISTORY,
  CLEANUP_APPLICANT_DETAIL,
} from '../../../actions-index';
import {
  ERROR_CONSTS,
  RECOMMENDATION_HISTORY_UPDATES,
  STATUS,
} from '../../../util/constants';

// ---------------------------------------------------------------------------
// Initial state — mirrors the legacy studentData reducer
// ---------------------------------------------------------------------------
const initialState = {
  status: '',
  alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT,
};

const initialFullState = {
  student: { ...initialState, type: FETCH_STUDENT },
  applicationDocuments: { ...initialState, data: [], type: SEARCH_DOCUMENTS },
  documentFiles: { ...initialState, documents: [], type: GET_DOCUMENT },
};

// ---------------------------------------------------------------------------
// studentData slice
// ---------------------------------------------------------------------------
const applicantSlice = createSlice({
  name: 'studentData',
  initialState: initialFullState,
  reducers: {
    cleanupApplicantDetail() {
      return initialFullState;
    },

    fetchStudent(state, action) {
      const { datetimePageLoad } = action.payload ?? {};

      // SAS-31 Skip if we've navigated away from the page
      if (
        action.payload.status !== STATUS.LOADING &&
        (state.student.status !== STATUS.LOADING ||
          state?.student?.datetimePageLoad !== datetimePageLoad)
      ) {
        return state;
      }

      return {
        ...state,
        student: {
          status: action.payload.status,
          alert: action.payload.alert,
          data: {
            ...action.payload.data,
            expectedRecommendation:
              action.payload.data?.expectedRecommendation ??
              state.student?.data?.expectedRecommendation,
          },
          datetimePageLoad,
          type: FETCH_STUDENT,
        },
      };
    },

    searchDocuments(state, action) {
      return {
        ...state,
        applicationDocuments: {
          status: action.payload.status,
          showRefresh: action.payload.status
            ? action.payload.status === STATUS.ERROR
            : state.applicationDocuments?.showRefresh,
          alert: action.payload.alert,
          data: action.payload.data ?? [],
          type: SEARCH_DOCUMENTS,
        },
      };
    },

    setExpectedRecommendation(state, action) {
      return {
        ...state,
        student: {
          ...state?.student,
          status: action.payload.status,
          alert: action.payload.alert,
          type: EXPECTED_RECOMMENDATION,
          data: {
            ...state?.student?.data,
            expectedRecommendation: action.payload.data,
          },
        },
      };
    },

    getDocument(state, action) {
      const { datetimeDownloadRequested } = action.payload ?? {};

      // SAS-31 Ignore if they've navigated away from the applicant detail page
      if (
        !datetimeDownloadRequested ||
        !state?.student?.data?.demographics?.applicantEmployeeId ||
        (action.payload.status !== STATUS.LOADING &&
          state?.documentFiles?.datetimeDownloadRequested !==
            datetimeDownloadRequested)
      ) {
        return state;
      }

      return {
        ...state,
        documentFiles: {
          status: action.payload.status,
          alert: action.payload.alert,
          data: action.payload.data,
          datetimeDownloadRequested,
          documents: action.payload.documents,
          type: GET_DOCUMENT,
        },
      };
    },

    updateRecommendationHistory(state, action) {
      switch (action.payload.actionType) {
        case RECOMMENDATION_HISTORY_UPDATES.ASSIGN:
          return {
            ...state,
            student: {
              ...state?.student,
              data: {
                ...state?.student?.data,
                readers: [
                  ...state?.student?.data?.readers,
                  {
                    readerEmployeeId:
                      action.payload.user.reviewerEmployeeId,
                    readerFirstName: action.payload.user.firstName,
                    readerLastName: action.payload.user.lastName,
                    status: STATUS.ASSIGNED,
                  },
                ],
                recommendationHistory: [
                  ...state?.student?.data?.recommendationHistory,
                  action.payload.user,
                ],
              },
            },
          };
        case RECOMMENDATION_HISTORY_UPDATES.UNASSIGN:
          return {
            ...state,
            student: {
              ...state?.student,
              data: {
                ...state?.student?.data,
                readers: state?.student?.data?.readers?.map((reviewer) => {
                  if (
                    reviewer.readerEmployeeId ===
                    action.payload.reviewerEmployeeId
                  ) {
                    return {
                      ...reviewer,
                      status: STATUS.CANCELLED,
                      data: STATUS.CANCELLED,
                    };
                  } else {
                    return reviewer;
                  }
                }),
                recommendationHistory:
                  state?.student?.data?.recommendationHistory?.map(
                    (reviewer) => {
                      if (
                        reviewer.reviewerEmployeeId ===
                        action.payload.reviewerEmployeeId
                      ) {
                        return {
                          ...reviewer,
                          recommendation: 'Cancelled',
                          recommendationUnified: 'Cancelled',
                        };
                      } else {
                        return reviewer;
                      }
                    }
                  ),
              },
            },
          };
        default:
          return state;
      }
    },
  },
});

export const {
  cleanupApplicantDetail,
  fetchStudent,
  searchDocuments,
  setExpectedRecommendation,
  getDocument,
  updateRecommendationHistory,
} = applicantSlice.actions;

// ---------------------------------------------------------------------------
// studentDataWithLegacy — wraps the slice reducer to also handle legacy
// action types dispatched by both the legacy thunks and the AlertBar dismiss
// flow. Legacy thunks dispatch { type: FETCH_STUDENT | SEARCH_DOCUMENTS |
// GET_DOCUMENT, payload: { status, alert, data, ... } }.
// ---------------------------------------------------------------------------
const baseReducer = applicantSlice.reducer;

export const studentData = (state, action) => {
  switch (action.type) {
    case FETCH_STUDENT: {
      const { datetimePageLoad } = action.payload ?? {};

      // AlertBar dismiss: no datetimePageLoad, just clear status/alert
      if (!datetimePageLoad) {
        return {
          ...state,
          student: {
            ...state.student,
            status: action.payload.status,
            alert: action.payload.alert,
            type: FETCH_STUDENT,
          },
        };
      }

      // SAS-31 Skip if we've navigated away from the page
      if (
        action.payload.status !== STATUS.LOADING &&
        (state.student.status !== STATUS.LOADING ||
          state?.student?.datetimePageLoad !== datetimePageLoad)
      ) {
        return state;
      }

      return {
        ...state,
        student: {
          status: action.payload.status,
          alert: action.payload.alert,
          data: {
            ...action.payload.data,
            expectedRecommendation:
              action.payload.data?.expectedRecommendation ??
              state.student?.data?.expectedRecommendation,
          },
          datetimePageLoad,
          type: FETCH_STUDENT,
        },
      };
    }
    case SEARCH_DOCUMENTS:
      return {
        ...state,
        applicationDocuments: {
          status: action.payload.status,
          showRefresh: action.payload.status
            ? action.payload.status === STATUS.ERROR
            : state.applicationDocuments?.showRefresh,
          alert: action.payload.alert,
          data: action.payload.data ?? [],
          type: SEARCH_DOCUMENTS,
        },
      };
    case GET_DOCUMENT: {
      const { datetimeDownloadRequested } = action.payload ?? {};

      // AlertBar dismiss: no datetimeDownloadRequested, just clear status/alert
      if (!datetimeDownloadRequested) {
        return {
          ...state,
          documentFiles: {
            ...state.documentFiles,
            status: action.payload.status,
            alert: action.payload.alert,
            type: GET_DOCUMENT,
          },
        };
      }

      // SAS-31 Ignore if they've navigated away from the applicant detail page
      if (
        !state?.student?.data?.demographics?.applicantEmployeeId ||
        (action.payload.status !== STATUS.LOADING &&
          state?.documentFiles?.datetimeDownloadRequested !==
            datetimeDownloadRequested)
      ) {
        return state;
      }

      return {
        ...state,
        documentFiles: {
          status: action.payload.status,
          alert: action.payload.alert,
          data: action.payload.data,
          datetimeDownloadRequested,
          documents: action.payload.documents,
          type: GET_DOCUMENT,
        },
      };
    }
    case CLEANUP_APPLICANT_DETAIL:
      return initialFullState;
    case EXPECTED_RECOMMENDATION:
      return {
        ...state,
        student: {
          ...state?.student,
          status: action.payload.status,
          alert: action.payload.alert,
          type: EXPECTED_RECOMMENDATION,
          data: {
            ...state?.student?.data,
            expectedRecommendation: action.payload.data,
          },
        },
      };
    case UPDATE_RECOMMENDATION_HISTORY:
      switch (action.payload.actionType) {
        case RECOMMENDATION_HISTORY_UPDATES.ASSIGN:
          return {
            ...state,
            student: {
              ...state?.student,
              data: {
                ...state?.student?.data,
                readers: [
                  ...(state?.student?.data?.readers ?? []),
                  {
                    readerEmployeeId: action.payload.user.reviewerEmployeeId,
                    readerFirstName: action.payload.user.firstName,
                    readerLastName: action.payload.user.lastName,
                    status: STATUS.ASSIGNED,
                  },
                ],
                recommendationHistory: [
                  ...(state?.student?.data?.recommendationHistory ?? []),
                  action.payload.user,
                ],
              },
            },
          };
        case RECOMMENDATION_HISTORY_UPDATES.UNASSIGN:
          return {
            ...state,
            student: {
              ...state?.student,
              data: {
                ...state?.student?.data,
                readers: state?.student?.data?.readers?.map((reviewer) => {
                  if (reviewer.readerEmployeeId === action.payload.reviewerEmployeeId) {
                    return { ...reviewer, status: STATUS.CANCELLED, data: STATUS.CANCELLED };
                  }
                  return reviewer;
                }),
                recommendationHistory: state?.student?.data?.recommendationHistory?.map((reviewer) => {
                  if (reviewer.reviewerEmployeeId === action.payload.reviewerEmployeeId) {
                    return { ...reviewer, recommendation: 'Cancelled', recommendationUnified: 'Cancelled' };
                  }
                  return reviewer;
                }),
              },
            },
          };
        default:
          return state;
      }
    default:
      return baseReducer(state, action);
  }
};
